import { PortOrAddress } from 'features/Directory'
import { Factory } from 'fishery'

import { Cargo, FCLCargoJSON, LCLCargoJSON } from '../../Cargo'
import { BookingParty } from '../../Incoterms/incotermsMeta'
import { CarbonOffsetStrategy, IQuoteServices } from '../../QuoteServices'
import { IQuoteSheet } from '../../QuoteSheet/QuoteSheetModel'
import { IQuoteOption, Route } from '../../QuoteSheet/QuoteSheetOption'
import { QuoteRequestFormState } from '../quoteRequestUtils'

export const portOrAddressFactory = Factory.define<PortOrAddress>(({ transientParams }) => {
  return {
    _id: '123',
    name: 'place',
    place_id: '123',
    location: {
      lat: 0,
      lng: 0,
    },
    description: 'place',
    types: ['place'],
    country: 'Germany',
    street: 'Street',
    street2: 'Street 2',
    streetNumber: '1',
    city: 'City',
    postalCode: '12345',
    state: 'State',
    countryCode: '',
    district: 'District',
    locationId: 'location-id',
    googplace_id: 'googplace-id',
    googlePlace: 'googlePlace',
    googplace_types: [],
    fullAddress: 'fullAddress',
    companyName: 'company',
    ...transientParams,
  }
})

const quoteServicesFactory = Factory.define<IQuoteServices>(({ transientParams }) => {
  return {
    customsSelected: false,
    customsTypes: [],
    customsIssuance: false,
    dangerousGoods: false,
    hsCodesCount: 1,
    insurances: [],
    ...transientParams,
  }
})

export const cargoFactory = Factory.define<Cargo>(({ transientParams }) => {
  return {
    type: 'fcl',
    containerType: '22GP',
    quantity: 1,
    ...transientParams,
  }
})

export const quoteRequestFactory = Factory.define<IQuoteSheet['request']>(({ transientParams }) => {
  return {
    customerId: 'FAKE_CUSTOMER_ID',
    userId: 'FAKE_USER_ID',
    type: 'fcl',
    journey: {
      origin: portOrAddressFactory.build(),
      destination: portOrAddressFactory.build(),
    },
    cargoReadyAt: new Date('2023-01-01T00:00:00.000Z'),
    ignorePreCarriageDuration: false,
    goodsValue: {
      value: 1000,
      code: 'USD',
    },
    modes: ['sea'],
    cargo: cargoFactory.buildList(1),
    cargoCount: 1,
    totalWeight: 1000,
    totalVolume: 1000,
    currencyExchangeRateId: 'FAKE_CURRENCY_EXCHANGE_RATE_ID',
    services: quoteServicesFactory.build(),
    isDemo: false,
    incoTerm: 'FOB',
    bookingParty: BookingParty.Buyer,
    ...transientParams,
  }
})

const routeFactory = Factory.define<Route>(({ transientParams }) => {
  return {
    journey: {
      origin: portOrAddressFactory.build(),
      destination: portOrAddressFactory.build(),
    },
    segments: [],
    ...transientParams,
  }
})

const quoteOptionFactory = Factory.define<IQuoteOption>(({ sequence, transientParams }) => {
  return {
    _id: sequence.toString(),
    internalIndex: 1,
    totalDuration: 12,
    type: 'fcl',
    mode: 'sea',
    totalEUR: 1000,
    isNotFak: false,
    lineItems: [],
    additionalCharges: 'additionalCharges',
    bookableUntil: new Date('2023-01-01T00:00:00.000Z'),
    routeQuotes: {
      mainQuote: {
        rates: [],
        mode: 'sea',
        route: routeFactory.build(),
        lineItems: [],
      },
    },
    ...transientParams,
  }
})

const quoteResponseFactory = Factory.define<IQuoteSheet['response']>(({ transientParams }) => {
  const quoteOptions = quoteOptionFactory.buildList(1)
  return {
    options: quoteOptions,
    option_ids: quoteOptions.map<string>((option) => option._id as string),
    validUntil: new Date('2023-01-01T00:00:00.000Z'),
    ...transientParams,
  }
})

export const quoteSheetFactory = Factory.define<IQuoteSheet>(({ sequence, transientParams }) => {
  return {
    _id: sequence.toString(),
    shipment_ids: [],
    createdAt: new Date('2023-01-01T00:00:00.000Z'),
    request: quoteRequestFactory.build(),
    response: quoteResponseFactory.build(),
    ...transientParams,
  }
})

export const quoteRequestFormStateFactory = Factory.define<QuoteRequestFormState>(({ transientParams }) => {
  return {
    cargoFcl: cargoFactory.buildList(1) as FCLCargoJSON[],
    cargoLcl: cargoFactory.buildList(1) as LCLCargoJSON[],
    cargoReadyAt: new Date('2023-01-01T00:00:00.000Z'),
    cargoStackable: false,
    customs: false,
    customsBondType: 'customsBondType',
    customsIssuance: false,
    dangerousGoods: false,
    dangerousGoodsAdditionalDetails: 'dangerousGoodsAdditionalDetails',
    destination: portOrAddressFactory.build(),

    goodsValue: {
      value: 1000,
      code: 'USD',
    },
    hsCodesCount: 1,
    insurance: [],
    insuranceEnabled: false,
    modes: ['sea'],
    origin: portOrAddressFactory.build(),
    specialEquipment: false,
    type: 'fcl',
    vgm: false,
    incoTerm: 'FOB',
    bookingParty: BookingParty.Buyer,
    carbonOffsetService: {
      offsetStrategy: CarbonOffsetStrategy.All,
    },
    isBookingPartyAgent: false,
    ...transientParams,
  }
})
